import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import config from '../../Common/Configurations/APIConfig';
import apiService from "../../Common/Configurations/APIService";
import Header from '../../Common/Layout/Header';
import { useAuth } from '../../Common/Configurations/AuthContext';
import DeleteAlert from '../../Common/Layout/DeleteAlert';
import moment from 'moment';
import { Button, Form, Row, Col, Card, Badge, InputGroup, Modal, Spinner } from 'react-bootstrap';
import LeadChat from '../../Components/Marketing/LeadChat';

// --- ENHANCED STYLES FOR COLORFUL & PROFESSIONAL UI ---
const styles = {
    pageBg: { backgroundColor: "#f3f4f6", minHeight: "100vh" },

    // Right Side Colors
    summaryStrip: {
        background: "linear-gradient(135deg, #0ab39c 0%, #405189 100%)",
        color: "#fff",
        borderRadius: "8px"
    },
    gridLabel: {
        color: "#878a99",
        fontSize: "10px",
        textTransform: "uppercase",
        fontWeight: "700",
        letterSpacing: "0.5px"
    },
    gridValue: {
        color: "#212529",
        fontWeight: "600",
        fontSize: "13px"
    }
};

// Helper to get Status Color for Borders
const getStatusColor = (status) => {
    switch (status) {
        case 'NEW': return '#f06548'; // Red
        case 'PROCESSING': return '#f7b84b'; // Yellow/Orange
        case 'WON': return '#0ab39c'; // Green
        case 'LOST': return '#212529'; // Black/Dark
        default: return '#405189'; // Blue
    }
};

const HistoryViewModal = ({ show, onHide, history }) => {
    return (
        <Modal show={show} onHide={onHide} centered scrollable size="md">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fs-15 fw-bold text-uppercase">Lead Interaction History</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                {history && history.length > 0 ? (
                    <div className="acitivity-timeline acitivity-main">
                        {history.map((log, index) => (
                            <div className="acitivity-item d-flex mb-3" key={index}>
                                <div className="flex-grow-1 ms-3 border-bottom pb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <h6 className="mb-0 fw-bold">Status: {log.status}</h6>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <h6 className="mb-0 fw-bold">Message: {log.message}</h6>
                                        <small className="text-muted">{moment(log.createdDate).format("DD MMM, HH:mm")}</small>
                                    </div>
                                    <small className="fw-medium text-primary">
                                        <i className="ri-user-star-line me-1"></i> Updated by: {log.userName || "Unknown"}
                                    </small>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-center text-muted">No history logs found.</p>
                )}
            </Modal.Body>
        </Modal>
    );
};

const DmcEnquiryList = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    const [lists, setLists] = useState([]);
    const [activeTab, setActiveTab] = useState('leads'); // 'leads' or 'deals'

    const [selectedItem, setSelectedItem] = useState(null);
    const [editData, setEditData] = useState({});
    
    // Filters & Search
    const [searchTerm, setSearchTerm] = useState("");
    const [localSearch, setLocalSearch] = useState("");
    const [fromDate, setFromDate] = useState(moment().subtract(1, 'months').format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
    
    // UI States
    const [isListLoading, setIsListLoading] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isChatOpen, setIsChatOpen] = useState(false);

    // Fetch Data
    const GetData = async () => {
        if (!user) return;

        try {
            const tabParam = activeTab === 'deals' ? 'DEALS' : 'LEADS';
            const url = `${config.GET_LEADS_BY_TAB}/${user.id}?tabType=${tabParam}&enquiryType=PACKAGE&search=${localSearch}&fromDate=${fromDate}&toDate=${toDate}`;

            const res = await apiService.getAll(url);
            const data = Array.isArray(res) ? res : (res.data || []);
            setLists(data);
        } catch (error) {
            console.error(error);
            setLists([]);
        } finally {
            setIsListLoading(false);
        }
    };

    // --- Tab Change Effect ---
    useEffect(() => {
        if (user) {
            setSelectedItem(null);
            setIsListLoading(true);
            setLists([]);
            GetData();
        }
    }, [user, activeTab]);

    const handleSelectItem = (item) => {
        setSelectedItem(item);
        setEditData(item ? { ...item } : {});
        setIsChatOpen(false); // Reset chat view when switching items
    };

    const handleLocalChange = (field, value) => {
        setEditData(prev => ({ ...prev, [field]: value }));
    };

    const updateCustomerPipeline = async () => {
        if (!selectedItem) return;

        const payload = {
            leadId: selectedItem.leadId,
            leadStatus: editData.leadStatus,
            callBack: editData.callBack,
            newLog: {
                userId: user.id,
                userName: user.name,
                status: editData.leadStatus,
                message: editData.comment || "Status updated"
            }
        };

        try {
            const res = await apiService.update(config.UPDATE_LEAD_STATUS, payload);

            if (res) {
                setEditData(prev => ({ ...prev, comment: "" }));
                await GetData();

                const updatedHistoryEntry = {
                    ...payload.newLog,
                    createdDate: new Date().toISOString()
                };

                setSelectedItem(prev => ({
                    ...prev,
                    leadStatus: editData.leadStatus,
                    callBack: editData.callBack,
                    history: prev.history ? [...prev.history, updatedHistoryEntry] : [updatedHistoryEntry]
                }));
            }
        } catch (error) {
            console.error("Update error:", error);
        }
    };

    const filteredData = lists.filter(item => item.name?.toLowerCase().includes(searchTerm.toLowerCase()));

    // Icon Helper
    const getSourceIcon = (source) => {
        const s = source?.toLowerCase() || "";
        if (s.includes("facebook")) return { icon: "ri-facebook-fill", bg: "#e8f1f9", color: "#1877f2" };
        if (s.includes("whatsapp")) return { icon: "ri-whatsapp-line", bg: "#e6fffa", color: "#25d366" };
        if (s.includes("website")) return { icon: "ri-global-line", bg: "#e0f2ff", color: "#00a3ff" };
        return { icon: "ri-user-line", bg: "#f3f4f6", color: "#6b7280" };
    };

    if (loading) return <div className="p-5 text-center">Loading Dashboard...</div>;

    return (
        <div id="layout-wrapper" style={styles.pageBg}>
            <Header User={user} activeSection="packages" />
            <HistoryViewModal
                show={showHistoryModal}
                onHide={() => setShowHistoryModal(false)}
                history={selectedItem?.history}
            />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        

                        {/* --- MAIN CONTAINER --- */}
                        <div className="row mt-3 g-0 shadow-lg rounded-4 overflow-hidden border border-light" style={{ height: '82vh', backgroundColor: "#fff" }}>

                            {/* --- 1. LEFT SIDEBAR (Colorful List) --- */}
                            <div className="col-lg-3 border-end d-flex flex-column h-100" style={{ backgroundColor: "#fcfcfc" }}>
                                <div className="p-3 bg-white border-bottom ">
                                    
                                    {/* Tabs */}
                                    <div className="nav nav-pills nav-justified bg-light p-1 rounded-3 mb-3">
                                        <div
                                            className={`nav-link py-1 small fw-bold cursor-pointer ${activeTab === 'leads' ? 'bg-white shadow-sm text-primary' : 'text-muted'}`}
                                            onClick={() => setActiveTab('leads')}
                                        >Leads</div>
                                        <div
                                            className={`nav-link py-1 small fw-bold cursor-pointer ${activeTab === 'deals' ? 'bg-white shadow-sm text-primary' : 'text-muted'}`}
                                            onClick={() => setActiveTab('deals')}
                                        >Deals</div>
                                    </div>

                                    {/* --- DATE FILTERS --- */}
                                    <Row className="g-2 mb-2">
                                        <Col xs={6}>
                                            <Form.Label className="small text-muted mb-0" style={{ fontSize: '9px' }}>FROM</Form.Label>
                                            <Form.Control type="date" size="sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                                        </Col>
                                        <Col xs={6}>
                                            <Form.Label className="small text-muted mb-0" style={{ fontSize: '9px' }}>TO</Form.Label>
                                            <Form.Control type="date" size="sm" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                                        </Col>
                                    </Row>

                                    {/* --- SEARCH BAR + BUTTON --- */}
                                    <InputGroup size="sm">
                                        <Form.Control
                                            className="bg-light border-end-0 shadow-none"
                                            placeholder="Search..."
                                            value={localSearch}
                                            onChange={(e) => setLocalSearch(e.target.value)}
                                        />
                                        <Button variant="primary" className="px-3" onClick={GetData}>
                                            <i className="ri-search-line"></i>
                                        </Button>
                                    </InputGroup>
                                </div>

                                {/* List Items or Loader */}
                                <div className="flex-grow-1 overflow-auto p-2 custom-scrollbar">
                                    {isListLoading ? (
                                        <div className="d-flex flex-column align-items-center justify-content-center h-50">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2 text-muted small">Loading {activeTab}...</p>
                                        </div>
                                    ) : (
                                        filteredData.length === 0 ? (
                                            <div className="text-center mt-5">
                                                <p className="text-muted">No {activeTab} found.</p>
                                            </div>
                                        ) : (
                                            filteredData.map((item) => {
                                                const isActive = selectedItem?.leadId === item.leadId;
                                                const src = getSourceIcon(item.sourceName);
                                                const statusColor = getStatusColor(item.leadStatus);

                                                return (
                                                    <div
                                                        key={item.leadId}
                                                        onClick={() => handleSelectItem(item)}
                                                        className="p-3 mb-2 cursor-pointer position-relative"
                                                        style={{
                                                            marginBottom: "12px",
                                                            borderRadius: "8px",
                                                            transition: "all 0.2s ease",
                                                            background: isActive ? "linear-gradient(to right, #eef4ff, #ffffff)" : "#fff",
                                                            borderTop: isActive ? "1px solid #b7cced" : "1px solid #e9ebec",
                                                            borderRight: isActive ? "1px solid #b7cced" : "1px solid #e9ebec",
                                                            borderBottom: isActive ? "1px solid #b7cced" : "1px solid #e9ebec",
                                                            borderLeft: `4px solid ${isActive ? "#405189" : statusColor}`,
                                                            boxShadow: isActive ? "0 4px 6px rgba(0,0,0,0.05)" : "none"
                                                        }}
                                                    >
                                                        <div className="d-flex">
                                                            <div className="d-flex flex-column align-items-center me-3" style={{ minWidth: '50px' }}>
                                                                <div className="avatar-sm rounded-circle d-flex align-items-center justify-content-center mb-1"
                                                                    style={{ backgroundColor: src.bg, color: src.color }}>
                                                                    <i className={`${src.icon} fs-20`}></i>
                                                                </div>
                                                                <small className="text-muted text-center fw-bold" style={{ fontSize: '9px', lineHeight: '1.1' }}>
                                                                    {item.sourceName || "Agent"}
                                                                </small>
                                                            </div>

                                                            <div className="flex-grow-1 overflow-hidden">
                                                                <div className="d-flex justify-content-between align-items-start mb-1">
                                                                    <h6 className="mb-0 text-truncate fw-bold text-dark" style={{ fontSize: '14px', maxWidth: '65%' }} title={item.name}>{item.name}</h6>
                                                                    <span className="badge rounded-pill" style={{ backgroundColor: statusColor, fontSize: '9px' }}>{item.leadStatus}</span>
                                                                </div>
                                                                <div className="mb-2">
                                                                    <div className="d-flex align-items-center text-black small mb-1" title={item.email}>
                                                                        <i className="ri-mail-line me-2 text-primary"></i>
                                                                        <span className="text-truncate">{item.email || "--"}</span>
                                                                    </div>
                                                                    <div className="d-flex align-items-center text-black small" title={item.phoneNumber}>
                                                                        <i className="ri-phone-line me-2 text-success"></i>
                                                                        <span className="text-truncate">{item.phoneNumber || "--"}</span>
                                                                    </div>
                                                                </div>

                                                                <div className="d-flex justify-content-between border-top pt-2 mt-1">
                                                                    <small className="text-black" style={{ fontSize: '10px' }}>
                                                                        Cr: <span className="fw-bold">{moment(item.createdDate).format("DD MMM HH:mm")}</span>
                                                                    </small>
                                                                    <small className="text-black" style={{ fontSize: '10px' }}>
                                                                        Upd: <span className="fw-bold">{moment(item.updatedDate).format("DD MMM HH:mm")}</span>
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )
                                    )}
                                </div>
                            </div>

                            {/* --- 2. WORKSPACE (RIGHT) --- */}
                            <div className="col-lg-9 d-flex flex-column h-100" style={{ backgroundColor: "#f8f9fa" }}>
                                {selectedItem ? (
                                    <>
                                        {/* HEADER */}
                                        <div className="p-4 shadow-sm d-flex justify-content-between align-items-center bg-white border-bottom" style={styles.summaryStrip}>
                                            <div className="d-flex align-items-center">
                                                <div className="avatar-md text-white rounded-circle d-flex align-items-center justify-content-center me-3 fw-bold fs-20 shadow-lg"
                                                    style={{ background: "linear-gradient(135deg, #405189 0%, #0ab39c 100%)" }}>
                                                    {selectedItem.name?.charAt(0)}
                                                </div>
                                                <div>
                                                    <h4 className="fw-bold mb-1 text-dark">{selectedItem.name}</h4>
                                                    <div className="d-flex align-items-center text-white">
                                                        <i className="ri-phone-line me-1 text-success"></i> {selectedItem.phoneNumber || "--"}
                                                        <span className="mx-2 text-black">|</span>
                                                        <i className="ri-mail-line me-1 text-primary"></i> {selectedItem.email}
                                                        <span className="mx-2 text-black">|</span>
                                                        <i className="ri-map-pin-line me-1 text-danger"></i> {selectedItem.enquiryType || "Unknown"}
                                                        <span className="mx-2 text-black">|</span>
                                                        <i className="ri-calendar-line me-1 text-info"></i> {moment(selectedItem.createdDate).format("DD MMM YYYY")}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2">
                                                {activeTab === 'leads' ? (
                                                    <>
                                                        <Button variant="success" size="sm" className="rounded-pill px-3 fw-bold" onClick={() => window.open(`https://web.whatsapp.com/send?phone=${selectedItem.phoneNumber}`)}><i className="ri-whatsapp-line me-1"></i> WhatsApp</Button>
                                                        <Button variant="primary" size="sm" className="rounded-pill px-3 fw-bold" onClick={() => window.location.href = `mailto:${selectedItem.email}`}><i className="ri-mail-line me-1"></i> Email</Button>
                                                        <Button variant="danger" size="sm" className="rounded-circle avatar-xs p-0" onClick={() => { setItemToDelete(selectedItem.leadId); setShowDelete(true); }}><i className="ri-delete-bin-line"></i></Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button variant="primary" size="sm" className="shadow-sm" onClick={() => navigate('/dmcpackagecreate', { state: { leadId: selectedItem.leadId } })}><i className="ri-add-line"></i> Create Package</Button>
                                                        
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="p-2 overflow-auto flex-grow-1">
                                            <Row className="gy-4">

                                                {/* REQUIREMENTS CARD */}
                                                <Col md={9}>
                                                    <Card className="border-0 shadow-sm rounded-3 h-70 overflow-hidden">
                                                        <Card.Body className="p-0">
                                                            <div className="p-4 bg-white">
                                                                <h6 className="fw-bold text-dark mb-3 text-uppercase small ls-1">Detailed Requirements</h6>
                                                                <div className="border rounded overflow-hidden">
                                                                    <Row className="g-0">
                                                                        {Object.entries(selectedItem.serviceDetails || {}).map(([key, value], index) => {
                                                                            const displayKey = key.replace(/([A-Z])/g, ' $1').trim();
                                                                            const isBoolean = value?.toString().toLowerCase() === "true" || value?.toString().toLowerCase() === "false";
                                                                            const isPositive = value?.toString().toLowerCase() === "true";

                                                                            return (
                                                                                <Col xs={6} md={3} key={key} className={`p-3 border-bottom ${index % 4 !== 3 ? 'border-end' : ''}`} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#fcfcfc' }}>
                                                                                    <div style={styles.gridLabel}>{displayKey}</div>
                                                                                    {isBoolean ? (
                                                                                        <Badge bg={isPositive ? "success" : "light"} className={`mt-1 ${isPositive ? "" : "text-muted border"}`}>
                                                                                            {isPositive ? "YES" : "NO"}
                                                                                        </Badge>
                                                                                    ) : (
                                                                                        <div style={styles.gridValue} className="mt-1 text-truncate" title={value?.toString()}>{value?.toString() || "-"}</div>
                                                                                    )}
                                                                                </Col>
                                                                            );
                                                                        })}
                                                                    </Row>
                                                                    <div className="px-4 py-3 bg-white border-bottom">
                                                                        <div className="d-flex">
                                                                            <i className="ri-double-quotes-l text-primary fs-20 me-2 opacity-50"></i>
                                                                            <p className="mb-0 text-black fst-bold">{selectedItem.description || "No specific customer requirements."}</p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>

                                                {/* ACTION SIDEBAR */}
                                                <Col md={3}>
                                                    <Card className="shadow-none border rounded-3 mb-3">
                                                        <Card.Header className="bg-light py-2 d-flex justify-content-between align-items-center">
                                                            <h6 className="mb-0 fw-bold small text-primary">STATUS CONTROL</h6>
                                                            <Link to="#" onClick={() => setShowHistoryModal(true)} title="View Audit Trail"><i className="ri-history-line fs-16 text-muted">View History</i></Link>
                                                        </Card.Header>
                                                        <Card.Body className="p-3">
                                                            <Form.Group className="mb-2"><Form.Label className="text-muted small fw-bold">STATUS</Form.Label><Form.Select size="sm" value={editData.leadStatus || ""} onChange={e => handleLocalChange('leadStatus', e.target.value)}><option value="NEW">NEW</option><option value="PROCESSING">PROCESSING</option><option value="FOLLOW UP">FOLLOW UP</option><option value="WON">WON</option><option value="LOST">LOST</option></Form.Select></Form.Group>
                                                            <Form.Group className="mb-2"><Form.Label className="text-muted small fw-bold">NEXT CALL</Form.Label><Form.Control size="sm" type="datetime-local" value={editData.callBack ? moment(editData.callBack).format("YYYY-MM-DDTHH:mm") : ""} onChange={e => handleLocalChange('callBack', e.target.value)} /></Form.Group>

                                                            <Form.Group className="mb-2">
                                                                <Form.Label className="text-muted small fw-bold">NOTE / COMMENT</Form.Label>
                                                                <Form.Control as="textarea" rows={2} size="sm" placeholder="Type status update..." value={editData.comment || ""} onChange={e => handleLocalChange('comment', e.target.value)} />
                                                            </Form.Group>

                                                            <Button variant="primary" size="sm" className="w-100 fw-bold mt-2" onClick={updateCustomerPipeline}>Update Status</Button>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>

                                                {/* --- AGENT INTERACTION HUB (Available for BOTH Leads and Deals) --- */}
                                                <Col md={12}>
                                                    <Card className="shadow-lg border-0 rounded-4 overflow-hidden" style={{ height: "500px", background: "#ffffff" }}>
                                                        {/* FULL WIDTH AGENT INTERACTION HUB */}
                                                        <div className="d-flex flex-column h-100">
                                                            {isChatOpen ? (
                                                                <LeadChat
                                                                    leadId={selectedItem.leadId}
                                                                    title={`Chat with Agent (Re: ${selectedItem.name})`}
                                                                    user={user}
                                                                    type="DMC" // Identifying as DMC
                                                                    onClose={() => setIsChatOpen(false)}
                                                                />
                                                            ) : (
                                                                <>
                                                                    {/* Header for Chat Section */}
                                                                    <div className="p-4 border-bottom bg-soft-light d-flex justify-content-between align-items-center">
                                                                        <div className="d-flex align-items-center">
                                                                            <div className="avatar-sm bg-gradient-primary rounded-circle d-flex align-items-center justify-content-center me-3 text-white fw-bold shadow">
                                                                                <i className="ri-user-voice-line fs-20"></i>
                                                                            </div>
                                                                            <div>
                                                                                <h5 className="mb-0 fw-bold text-dark">Agent Interaction Hub</h5>
                                                                                <small className="text-muted">Direct communication channel with the Lead Owner</small>
                                                                            </div>
                                                                        </div>
                                                                        <Button variant="primary" className="px-4 shadow-sm rounded-pill" onClick={() => setIsChatOpen(true)}>
                                                                            <i className="ri-chat-3-line me-2"></i> Start Conversation
                                                                        </Button>
                                                                    </div>
                                                                    
                                                                    {/* Empty State / Prompt */}
                                                                    <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center p-5" 
                                                                         style={{ background: "linear-gradient(180deg, #f8fafc, #ffffff)" }}>
                                                                        <img src="https://cdn-icons-png.flaticon.com/512/3063/3063822.png" alt="Discussion" width="90" className="opacity-50 mb-4" />
                                                                        <h4 className="fw-bold text-dark">Questions about this Enquiry?</h4>
                                                                        <p className="text-muted text-center" style={{ maxWidth: "500px" }}>
                                                                            Use this space to share itineraries, negotiate pricing, and confirm details directly with the Agent.
                                                                            All conversations are logged for this specific ID.
                                                                        </p>
                                                                        <Button variant="outline-primary" className="rounded-pill px-4 mt-3" onClick={() => setIsChatOpen(true)}>
                                                                            Open Chat Window
                                                                        </Button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </Card>
                                                </Col>
                                            </Row>
                                        </div>
                                    </>
                                ) : (
                                    // EMPTY STATE / WELCOME SCREEN
                                    <div className="h-100 d-flex flex-column align-items-center justify-content-center">
                                        <div className="mb-3 avatar-lg bg-white rounded-circle shadow-sm d-flex align-items-center justify-content-center">
                                            <i className="ri-inbox-line fs-36 text-primary"></i>
                                        </div>
                                        <h5 className="text-dark fw-bold">Welcome to DMC Enquiry Manager</h5>
                                        <p className="text-muted">Select a {activeTab} from the inbox to display details.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <DeleteAlert show={showDelete} handleClose={() => setShowDelete(false)} handleDelete={async () => { await apiService.remove(config.DELETELEAD, itemToDelete); setShowDelete(false); GetData(); setSelectedItem(null); }} />
            </div>
        </div>
    );
};

export default DmcEnquiryList;